import React, { useRef, useState, useEffect, SyntheticEvent } from 'react';
import { API, Auth } from 'aws-amplify';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import FormControl from 'react-bootstrap/FormControl';
import Form from 'react-bootstrap/Form';
import { v4 as uuidv4 } from 'uuid';
import Alert from 'react-bootstrap/Alert';
import Content from './Content';
import { fetchPodcasts } from '../utils/api';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { prosodyRateValues, voices } from '../utils/voices-data';
import { sortArrayAtoZ } from '../utils/utils';

type Props = {
  loading: boolean;
};

const UploadXML = (props: Props): JSX.Element => {
  const documentId = useRef<HTMLInputElement>(null);
  const customerId = useRef<HTMLSelectElement>(null);
  const [reqvaluedocId, setReqvaluedocId] = useState(false);
  const [reqvaluecustId, setReqvaluecustId] = useState(false);
  const [reqvaluefile, setReqvaluefile] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertVariant, setAlertVariant] = useState('success');
  const [message, setMessage] = useState('');
  const [validated, setValidated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('default');
  const [selectedSettings, setSelectedSettings] = useState({});
  const { podcasts } = useAppSelector((state) => state.podcasts);
  const customersListJson = process.env.REACT_APP_CUSTOMER_IDS_URL;
  const [customerIds] = useState(process.env.REACT_APP_CUSTOMER_IDS?.split(', ')!);

  const dispatch = useAppDispatch();

  const voiceIdRef = useRef<HTMLSelectElement>(null);
  const enginesRef = useRef<HTMLSelectElement>(null);
  const domainsRef = useRef<HTMLSelectElement>(null);
  const speakingRateRef = useRef<HTMLSelectElement>(null);

  const defaultSettings = {
    prosodyRate: 110,
    languages: {
      'en-US': { voiceId: 'Matthew', engine: 'neural', domain: 'empty', engines: ['neural', 'standard'], domains: [] },
      'en-GB': { voiceId: 'Amy', engine: 'neural', domain: 'empty', engines: ['neural', 'standard'], domains: [] },
      'ja-JP': { voiceId: 'Takumi', engine: 'standard', domain: 'empty', engines: ['neural', 'standard'], domains: [] },
      'zh-CN': { voiceId: 'Zhiyu', engine: 'neural', domain: 'empty', engines: ['neural', 'standard'], domains: [] },
      'es-ES': { voiceId: 'Lucia', engine: 'neural', domain: 'empty', engines: ['neural', 'standard'], domains: [] },
      'es-MX': { voiceId: 'Mia', engine: 'neural', domain: 'empty', engines: ['neural', 'standard'], domains: [] },
      'es-US': { voiceId: 'Lupe', engine: 'neural', domain: 'empty', engines: ['neural', 'standard'], domains: [] },
    },
  };

  const filterVoiceByLanguage = (value: string) => {
    if (value === 'default') return;
    const voice = voices[value];
    return voice;
  };

  useEffect(() => {
    console.log(filterVoiceByLanguage(selectedLanguage));
    if (selectedLanguage !== 'default') {
      setSelectedSettings(voices[selectedLanguage][0]);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    console.log(selectedSettings);
  }, [selectedSettings]);

  useEffect(() => {
    if (customersListJson && customersListJson.startsWith('http')) {
      fetchPodcasts(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateUID = async () => {
    const node = documentId.current;
    if (node) {
      node.value = uuidv4();
    }
  };

  const uploadFile = async () => {
    const documentNode = documentId.current;
    const customerNode = customerId.current;
    const regexpDocumentId = new RegExp(/^[a-zA-Z0-9_\-]+$/gi);

    let pollyParams = {};

    if (selectedLanguage !== 'default') {
      const provider = selectedSettings['provider'];
      const domain = domainsRef.current?.value === 'empty' ? '' : domainsRef.current?.value;
      pollyParams = {
        engine: enginesRef.current?.value,
        languageCode: selectedLanguage,
        speekingRate: speakingRateRef.current?.value,
        provider: provider,
        sampleRate: 24000,
        voiceId: voiceIdRef.current?.value,
        domain: domain,
        pitch: 100,
      };
    }

    if (documentNode && customerNode) {
      setShowAlert(false);
      if (documentNode.value === null || documentNode.value.trim() === '') {
        setReqvaluedocId(true);
        return;
      } else if (!regexpDocumentId.test(documentNode.value)) {
        setValidated(true);
        return;
      } else {
        setReqvaluedocId(false);
        setValidated(false);
      }

      if (customerNode.value === null || customerNode.value === '0') {
        setReqvaluecustId(true);
        return;
      } else {
        setReqvaluecustId(false);
      }

      if (selectedFile === null) {
        setReqvaluefile(true);
        return;
      } else {
        setReqvaluefile(false);
      }

      const myHeaders = new Headers();
      myHeaders.append('Authorization', `Bearer ${(await Auth.currentSession()).getAccessToken().getJwtToken()}`);
      let formdata = new FormData();
      formdata.append('documentId', documentNode.value);
      formdata.append('customerId', customerNode.value);
      formdata.append('xml', selectedFile, 'ms3.xml');
      if (selectedLanguage !== 'default') {
        formdata.append('pollyParams', JSON.stringify(pollyParams));
      }

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: formdata,
        redirect: 'follow',
      };

      const apiName = 'Columbia';
      const path = '/document';

      await API.post(apiName, path, requestOptions)
        .then((response) => {
          setSelectedFile(null);
          documentNode.value = '';
          customerNode.value = '0';

          setShowAlert(true);
          setAlertVariant('success');
          setMessage('Success');
        })
        .catch((error) => {
          console.log(error);
          setShowAlert(true);
          setAlertVariant('danger');
          setMessage('Error');
        });
    }
  };

  const change = (e: SyntheticEvent) => {
    const files = (e.target as HTMLInputElement).files;
    setSelectedFile(files ? files[0] : null);
    setReqvaluefile(false);
  };

  const sortPodcastIdArray = () => {
    if (podcasts && podcasts.length > 0) {
      const editablePodcastIdArray = [...podcasts];
      sortArrayAtoZ(editablePodcastIdArray);
      return editablePodcastIdArray;
    }
    return undefined;
  };

  const ArrowIcon = () => {
    if (isExpanded) {
      return <FontAwesomeIcon icon={faChevronUp} />;
    }
    return <FontAwesomeIcon icon={faChevronDown} />;
  };

  return (
    <Content loading={props.loading}>
      {showAlert ? (
        <Alert variant={alertVariant} dismissible onClose={() => setShowAlert(false)}>
          {message}
        </Alert>
      ) : (
        ''
      )}
      <Form>
        <Container className="mt-5">
          <Row className="justify-content-between align-items-center">
            <Col>
              <label htmlFor="documentId">Document ID</label>
              <InputGroup className="mb-3">
                <FormControl ref={documentId} />
                <InputGroup.Append>
                  <Button variant="info" onClick={generateUID}>
                    Generate UID
                  </Button>
                </InputGroup.Append>
                <Form.Control.Feedback
                  type="invalid"
                  style={{
                    display: `${reqvaluedocId || validated ? 'block' : 'none'}`,
                  }}
                >
                  Please insert a valid UID or press the button Generate a random one.
                </Form.Control.Feedback>
              </InputGroup>
              <Form.Group controlId="customerId">
                <Form.Label>Customer ID</Form.Label>
                <Form.Control as="select" ref={customerId}>
                  <option value={0}></option>
                  {podcasts !== undefined &&
                    podcasts?.length > 0 &&
                    !process?.env?.REACT_APP_TABS?.includes('NONE') &&
                    sortPodcastIdArray()?.map(
                      (c: {
                        id: string | number | readonly string[] | undefined;
                        title: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | null | undefined;
                      }) => (
                        <option key={c.id?.toString()} value={c.id}>
                          {c.id}
                        </option>
                      ),
                    )}
                  {process?.env?.REACT_APP_TABS?.includes('NONE') &&
                    customerIds.map((c) => (
                      <option key={c.toString()} value={c}>
                        {c}
                      </option>
                    ))}
                </Form.Control>
                <Form.Control.Feedback
                  type="invalid"
                  style={{
                    display: `${reqvaluecustId ? 'block' : 'none'}`,
                  }}
                >
                  Please select a Customer.
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.File label="XML" onChange={change} />
              </Form.Group>
              <Form.Control.Feedback
                type="invalid"
                style={{
                  display: `${reqvaluefile ? 'block' : 'none'}`,
                }}
              >
                Please select an XML File.
              </Form.Control.Feedback>
            </Col>
            <Col className="mt-4 ml-5">
              <Button variant="primary" onClick={uploadFile}>
                Upload
              </Button>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                variant="light"
                className="flex items-center"
                onClick={() => setIsExpanded((prevState) => !prevState)}
                style={{ height: '40px' }}
              >
                <span className="mr-4">Advanced settings</span>
                <ArrowIcon />
              </Button>
            </Col>
          </Row>

          {isExpanded && (
            <>
              <Row className="mt-3">
                <Col className="col-12">
                  <div>
                    <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">
                      Choose voice language:
                    </label>
                  </div>
                </Col>

                <Col className="col-xl-8 col-md-12">
                  <Form.Control
                    className="w-50"
                    as="select"
                    onChange={(e) => {
                      setSelectedLanguage(e.target.value);
                    }}
                    value={selectedLanguage === 'default' ? 'default' : selectedLanguage}
                  >
                    <option value={'default'}>Default</option>
                    <option value={'en-GB'}>English GB</option>
                    <option value={'en-US'}>English US</option>
                    <option value={'ja-JP'}>Japanese</option>
                    <option value={'zh-CN'}>Chinese</option>
                    <option value={'es-ES'}>Spanish European</option>
                    <option value={'es-MX'}>Spanish Mexican</option>
                    <option value={'es-US'}>Spanish US</option>
                  </Form.Control>
                </Col>
              </Row>

              {selectedLanguage !== 'default' && (
                <div>
                  <Row className="mt-3">
                    <Col className="">
                      <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">{`Voice ${selectedLanguage}:`}</label>
                    </Col>
                  </Row>
                  <Row>
                    <Col className="flex col-xl-8 col-md-12">
                      <div className="mr-2">
                        <Form.Control
                          as="select"
                          ref={voiceIdRef}
                          onChange={(e) => {
                            const voiceItem = voices[selectedLanguage];
                            const filteredItem = voiceItem.filter((item) => item.voiceId === e.target.value);
                            console.log('filteredItem', filteredItem, typeof filteredItem);
                            setSelectedSettings(filteredItem[0]);
                          }}
                        >
                          {selectedLanguage !== 'default' &&
                            voices[selectedLanguage]?.map((item) => (
                              <option key={`${item.voiceId} - ${item.provider}`} value={item.voiceId}>
                                {item.provider}: {item.voiceId} - {item.gender}
                              </option>
                            ))}
                        </Form.Control>
                      </div>

                      {selectedSettings && (
                        <>
                          <div className="mr-2">
                            <Form.Control as="select" ref={enginesRef}>
                              {selectedSettings['engines']?.map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                            </Form.Control>
                          </div>
                          <div style={{ display: 'block' }}>
                            <Form.Control
                              as="select"
                              ref={domainsRef}
                              defaultValue={
                                selectedSettings['domains']?.length > 0 ? selectedSettings['domains'][0] : 'empty'
                              }
                              defaultChecked={
                                selectedSettings['domains']?.length > 0 ? selectedSettings['domains'][0] : 'empty'
                              }
                              onLoad={() => {}}
                            >
                              {selectedSettings['domains']?.map((value) => (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              ))}
                              {selectedSettings['domains']?.length === 0 && (
                                <option key={`domain-empty`} value="empty">
                                  empty
                                </option>
                              )}
                            </Form.Control>
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              <Row className="mt-3">
                <Col className="col-xl-8 col-md-12">
                  <label className="block text-gray-800 font-bold md:text-right mb-1 md:mb-0 pr-4">prosodyRate:</label>
                </Col>
              </Row>
              <Row>
                <Col className="col-xl-8 col-md-12">
                  <Form.Control
                    as="select"
                    ref={speakingRateRef}
                    className="w-25"
                    defaultValue={defaultSettings.prosodyRate}
                  >
                    {prosodyRateValues.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </Form>
    </Content>
  );
};

export default UploadXML;
